import { type ShopifyVariant } from '@/store/useMapStore';

const STOREFRONT_DOMAIN = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN ?? 'geoforge3d.myshopify.com';
const STOREFRONT_TOKEN = import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN ?? '';
const STOREFRONT_API_VERSION = '2026-01';

const PRODUCT_QUERY = `
  query ProductVariants($handle: String!) {
    product(handle: $handle) {
      title
      variants(first: 50) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }
  }
`;

interface StorefrontVariantNode {
  id: string;
  title: string;
  price: { amount: string; currencyCode: string };
  availableForSale: boolean;
  selectedOptions: { name: string; value: string }[];
}

interface StorefrontProductResponse {
  data: {
    product: {
      title: string;
      variants: {
        edges: { node: StorefrontVariantNode }[];
      };
    } | null;
  };
  errors?: { message: string }[];
}

export async function fetchProductVariants(productHandle?: string): Promise<ShopifyVariant[]> {
  const handle = productHandle || import.meta.env.VITE_SHOPIFY_PRODUCT_HANDLE || '3d-custom-map-art';

  if (!STOREFRONT_TOKEN) {
    console.warn('[storefrontService] No storefront token configured, using mock data');
    return getMockVariants();
  }

  const url = `https://${STOREFRONT_DOMAIN}/api/${STOREFRONT_API_VERSION}/graphql.json`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({
      query: PRODUCT_QUERY,
      variables: { handle },
    }),
  });

  if (!response.ok) {
    throw new Error(`Storefront API request failed: ${response.status}`);
  }

  const json: StorefrontProductResponse = await response.json();

  if (json.errors?.length) {
    throw new Error(`Storefront API error: ${json.errors[0].message}`);
  }

  if (!json.data.product) {
    throw new Error(`Product not found: ${handle}`);
  }

  return json.data.product.variants.edges.map(({ node }) => ({
    id: node.id,
    title: node.title,
    price: node.price.amount,
    availableForSale: node.availableForSale,
    selectedOptions: node.selectedOptions,
  }));
}

function getMockVariants(): ShopifyVariant[] {
  const sizes = [
    { value: '4"x5"', price: 55 },
    { value: '8"x10"', price: 89 },
    { value: '12"x15"', price: 119 },
    { value: '16"x20"', price: 159 },
  ];

  const frames = [
    { value: 'Black', price: 0 },
    { value: 'White', price: 0 },
    { value: 'Natural', price: 10 },
    { value: 'Walnut', price: 20 },
  ];

  const variants: ShopifyVariant[] = [];
  let id = 1;

  for (const size of sizes) {
    for (const frame of frames) {
      variants.push({
        id: `gid://shopify/ProductVariant/${id}`,
        title: `${size.value} / ${frame.value}`,
        price: String(size.price + frame.price),
        availableForSale: true,
        selectedOptions: [
          { name: 'Size', value: size.value },
          { name: 'Frame', value: frame.value },
        ],
      });
      id++;
    }
  }

  return variants;
}
